import { EvergreenWealthLogo } from '@egw/assets'
import { useAuth } from '@egw/auth'
import { configClient } from '@egw/config'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import cx from 'classnames'
import { type FC, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { twMerge } from 'tailwind-merge'
import { isCopilotKitRuntimeAvailable } from '@egw/ai-chat'
import { type Config } from '../../../config/config'
import { NavigationItem } from '../navigation-item/navigation-item'

export interface VerticalNavigationProps {
  className?: string
  id?: string
  testId?: string
  ariaLabel?: string
  setIsChatOpen?: (_isChatOpen: boolean) => void
}

export const VerticalNavigation: FC<VerticalNavigationProps> = ({
  className,
  id,
  testId = 'vertical-nav',
  ariaLabel,
  setIsChatOpen,
}) => {
  const { t } = useTranslation('layouts')
  const config = configClient.getConfig() as Config

  const baseClass = cx('h-full w-64 bg-taupe-100 flex flex-col max-lg:pt-lg')

  const finalClasses = twMerge(baseClass, className)

  const { signOut } = useAuth()

  const navigate = useNavigate()

  const { pathname } = useLocation()

  const { flags } = useFlags<FeatureFlags>()

  // Check if CopilotKit is available before showing Evergreen Intelligence navigation item
  const isCopilotKitEnabled = useMemo(() => isCopilotKitRuntimeAvailable(), [])

  return (
    <nav className={finalClasses} id={id} data-testid={testId} aria-label={ariaLabel}>
      {/* Logo Section */}
      <button
        type="button"
        data-testid="vertical-nav-logo"
        name="vertical-navigation-logo"
        aria-label="Evergreen Wealth home"
        onClick={() => void navigate(WEALTH_ROUTES.OVERVIEW.path)}
      >
        <EvergreenWealthLogo
          className="evergreen-logo text-text-header mx-lg h-auto py-xl"
          testId="vertical-nav-logo"
        />
      </button>

      {/* Main Navigation */}
      <div className="flex-1 ">
        <NavigationItem
          label={t('navigation.home')}
          href={WEALTH_ROUTES.OVERVIEW.path}
          testId="nav-overview"
          isActive={pathname.startsWith(WEALTH_ROUTES.OVERVIEW.path)}
          onClick={() => {
            setIsChatOpen?.(false)
          }}
        />

        <NavigationItem
          label={t('navigation.accounts')}
          // HREF GATED BY FF FOR YEET_COASTAL SHOULD GO TO INVESTMENTS_OVERVIEW
          href={WEALTH_ROUTES.INVESTMENTS_OVERVIEW.path}
          testId="nav-accounts"
          isActive={
            pathname.startsWith('/checking') ||
            pathname.startsWith('/savings') ||
            pathname.startsWith(WEALTH_ROUTES.INVESTMENTS.path) ||
            pathname.startsWith(WEALTH_ROUTES.EVERGREEN_ACCOUNTS.path) ||
            pathname.startsWith(WEALTH_ROUTES.CASH_ACCOUNTS_OVERVIEW.path)
          }
        />

        {flags.rolloutPlaidLinkedAccounts ? (
          <NavigationItem
            label={t('navigation.linked_accounts')}
            href={WEALTH_ROUTES.LINKED_ACCOUNTS.path}
            testId="nav-accounts"
            isActive={pathname.startsWith(WEALTH_ROUTES.LINKED_ACCOUNTS.path)}
          />
        ) : null}

        <NavigationItem
          isActive={pathname.startsWith(WEALTH_ROUTES.TRANSFER.path)}
          label={t('navigation.transfer')}
          href={WEALTH_ROUTES.TRANSFER.path}
          testId="nav-transfer"
          isDisabled={config.env === 'demo-dev' || config.env === 'demo-prod'}
        />

        {flags.rolloutCopilotKit && isCopilotKitEnabled ? (
          <NavigationItem
            isActive={pathname.startsWith(WEALTH_ROUTES.EVERGREEN_INTELLIGENCE.path)}
            label={t('navigation.evergreen_intelligence')}
            href={WEALTH_ROUTES.EVERGREEN_INTELLIGENCE.path}
            testId="nav-evergreen-intelligence"
          />
        ) : null}

        {/* TODO: Add advisor navigation item and keep gated by FF*/}
        {/* <NavigationItem label={t('navigation.advisor')} size="default" onClick={NOOP} testId="nav-advisor" /> */}
      </div>

      {/* Bottom Navigation */}
      <div className="mt-auto">
        <NavigationItem
          isActive={pathname.startsWith(WEALTH_ROUTES.DOCUMENTS.path)}
          label={t('navigation.documents')}
          size="small"
          href={WEALTH_ROUTES.DOCUMENTS.path}
          testId="nav-documents"
        />

        <NavigationItem
          isActive={pathname.startsWith(WEALTH_ROUTES.SETTINGS.path)}
          label={t('navigation.settings')}
          size="small"
          href={WEALTH_ROUTES.SETTINGS.path}
          testId="nav-settings"
          isDisabled={config.env === 'demo-dev' || config.env === 'demo-prod'}
        />

        <NavigationItem
          isActive={pathname.startsWith(WEALTH_ROUTES.HELP_CENTER.path)}
          label={t('navigation.contact_us')}
          size="small"
          href={WEALTH_ROUTES.HELP_CENTER.path}
          testId="nav-contact"
        />

        <NavigationItem
          label={t('navigation.sign_out')}
          size="small"
          onClick={() => void signOut()}
          testId="nav-sign-out"
        />

        {/* Version Info */}
        <div className="p-lg">
          <p className="typo-primary-xs-light text-text-body">{t('common.version', { version: config.appVersion })}</p>
        </div>
      </div>
    </nav>
  )
}
